import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { OneShell } from "@/components/OneShell";
import { ModuleStubPage } from "@/pages/ModuleStubPage";
import { HomePage } from "@/pages/HomePage";
import { CrmOverviewPage } from "@/pages/crm/CrmOverviewPage";
import { CrmLeadsPage } from "@/pages/crm/CrmLeadsPage";
import { CrmLeadNewPage } from "@/pages/crm/CrmLeadNewPage";
import { CrmLeadDetailPage } from "@/pages/crm/CrmLeadDetailPage";
import { CrmLeadEditPage } from "@/pages/crm/CrmLeadEditPage";
import { CrmLeadConvertPage } from "@/pages/crm/CrmLeadConvertPage";
import { CrmLeadConvertedPage } from "@/pages/crm/CrmLeadConvertedPage";
import { CrmLeadsConvertedPage } from "@/pages/crm/CrmLeadsConvertedPage";
import { CrmAccountsPage } from "@/pages/crm/CrmAccountsPage";
import { CrmAccountDetailPage } from "@/pages/crm/CrmAccountDetailPage";
import { CrmAccountNewPage, CrmAccountNewIndividualPage } from "@/pages/crm/CrmAccountNewPage";
import { CrmContactsPage } from "@/pages/crm/CrmContactsPage";
import { CrmContactDetailPage } from "@/pages/crm/CrmContactDetailPage";
import { CrmContactNewPage } from "@/pages/crm/CrmContactNewPage";
import { CrmDealsPage } from "@/pages/crm/CrmDealsPage";
import { CrmDealDetailPage } from "@/pages/crm/CrmDealDetailPage";
import { CrmDealNewPage } from "@/pages/crm/CrmDealNewPage";
import { CrmImportPage } from "@/pages/crm/CrmImportPage";
import { CrmSearchPage } from "@/pages/crm/CrmSearchPage";
import { CrmAuditModulePage, CrmAuditPage, CrmAuditRecordPage } from "@/pages/crm/CrmAuditPages";
import { CrmUnknownRoutePage } from "@/pages/crm/CrmStubPages";
import { CrmSettingsPage } from "@/pages/crm/settings/CrmSettingsPage";
import { CrmLeadModuleSettingsPage } from "@/pages/crm/settings/CrmLeadModuleSettingsPage";
import { CrmLeadModuleFieldDetailPage } from "@/pages/crm/settings/CrmLeadModuleFieldDetailPage";
import { CrmSettingsModulesPage } from "@/pages/crm/settings/CrmSettingsModulesPage";
import { SettingsOverviewPage } from "@/pages/settings/SettingsOverviewPage";
import { SettingsUsersPage } from "@/pages/settings/SettingsUsersPage";
import { SettingsGroupsPage } from "@/pages/settings/SettingsGroupsPage";
import { SettingsAuditPage } from "@/pages/settings/SettingsAuditPage";
import { FinanceOverviewPage } from "@/pages/finance/FinanceOverviewPage";
import { FinanceAccountsPage } from "@/pages/finance/FinanceAccountsPage";
import { FinanceComingSoonPage } from "@/pages/finance/FinanceComingSoonPage";
import { FinanceCustomersPage } from "@/pages/finance/FinanceCustomersPage";
import { FinanceInvoicesPage } from "@/pages/finance/FinanceInvoicesPage";
import { FinanceItemsPage } from "@/pages/finance/FinanceItemsPage";
import { FinanceCustomerDetailPage } from "@/pages/finance/FinanceCustomerDetailPage";
import { FinanceCustomerNewPage } from "@/pages/finance/FinanceCustomerNewPage";
import { FinanceItemDetailPage } from "@/pages/finance/FinanceItemDetailPage";
import { FinanceItemNewPage } from "@/pages/finance/FinanceItemNewPage";
import { FinanceInvoiceDetailPage } from "@/pages/finance/FinanceInvoiceDetailPage";
import { FinanceInvoiceNewPage } from "@/pages/finance/FinanceInvoiceNewPage";
import { FinancePaymentsReceivedPage } from "@/pages/finance/FinancePaymentsReceivedPage";
import { FinanceVendorsPage } from "@/pages/finance/FinanceVendorsPage";
import { OrganizationOverviewPage } from "@/pages/organization/OrganizationOverviewPage";
import { OrganizationDetailPage } from "@/pages/organization/OrganizationDetailPage";
import { KnowledgeBasePage } from "@/pages/knowledge/KnowledgeBasePage";
import { KnowledgeArticlesPage } from "@/pages/knowledge/KnowledgeArticlesPage";
import { KnowledgeArticlePage } from "@/pages/knowledge/KnowledgeArticlePage";
import { KnowledgeArticleViewPage } from "@/pages/knowledge/KnowledgeArticleViewPage";
import { KnowledgeLibrariesPage } from "@/pages/knowledge/KnowledgeLibrariesPage";
import { KnowledgeLibraryPage } from "@/pages/knowledge/KnowledgeLibraryPage";
import { KnowledgeBookPage } from "@/pages/knowledge/KnowledgeBookPage";
import { KnowledgeBooksPage } from "@/pages/knowledge/KnowledgeBooksPage";
import { KnowledgeLegacyRedirect } from "@/pages/knowledge/KnowledgeLegacyRedirect";
import { getActiveKnowledgeBaseId } from "@/lib/knowledgeBaseRegistry";
import { bookPath } from "@/lib/knowledgeModel";
import { resolveKnowledgeOrgId } from "@/lib/knowledgeOrgId";
import { refreshKnowledgeRegistryFromApi } from "@/lib/knowledgeRegistryCrossSync";
import { useSession } from "@/auth/SessionProvider";

const stub = (title: string, blurb: string) => (
  <ModuleStubPage title={title} blurb={blurb} />
);

export function App() {
  return (
    <Routes>
      <Route element={<OneShell />}>
        <Route index element={<HomePage />} />
        <Route path="/users" element={<SettingsUsersPage />} />
        <Route path="/crm/overview" element={<CrmOverviewPage />} />
        <Route path="/crm/leads/new" element={<CrmLeadNewPage />} />
        <Route path="/crm/leads/converted" element={<CrmLeadsConvertedPage />} />
        <Route path="/crm/leads/:leadId/convert" element={<CrmLeadConvertPage />} />
        <Route path="/crm/leads/:leadId/converted" element={<CrmLeadConvertedPage />} />
        <Route path="/crm/leads/:leadId/edit" element={<CrmLeadEditPage />} />
        <Route path="/crm/leads/:leadId" element={<CrmLeadDetailPage />} />
        <Route path="/crm/leads" element={<CrmLeadsPage />} />
        <Route path="/crm/accounts/new/individual" element={<CrmAccountNewIndividualPage />} />
        <Route path="/crm/accounts/new" element={<CrmAccountNewPage />} />
        <Route path="/crm/accounts/:accountId" element={<CrmAccountDetailPage />} />
        <Route path="/crm/accounts" element={<CrmAccountsPage />} />
        <Route path="/crm/contacts/new" element={<CrmContactNewPage />} />
        <Route path="/crm/contacts/:contactId" element={<CrmContactDetailPage />} />
        <Route path="/crm/contacts" element={<CrmContactsPage />} />
        <Route path="/crm/deals/new" element={<CrmDealNewPage />} />
        <Route path="/crm/deals/:dealId" element={<CrmDealDetailPage />} />
        <Route path="/crm/deals" element={<CrmDealsPage />} />
        <Route path="/crm/import" element={<CrmImportPage />} />
        <Route path="/crm/search" element={<CrmSearchPage />} />
        <Route
          path="/crm/settings/modules/lead/fields/:sectionSlug/:groupId"
          element={<CrmLeadModuleFieldDetailPage />}
        />
        <Route path="/crm/settings/modules/lead" element={<CrmLeadModuleSettingsPage />} />
        <Route
          path="/crm/settings/modules/accounts"
          element={stub("Accounts module settings", "Setup and fields for Accounts are coming soon.")}
        />
        <Route
          path="/crm/settings/modules/contacts"
          element={stub("Contacts module settings", "Setup and fields for Contacts are coming soon.")}
        />
        <Route
          path="/crm/settings/modules/deals"
          element={stub("Deals module settings", "Setup and fields for Deals are coming soon.")}
        />
        <Route path="/crm/settings/modules" element={<CrmSettingsModulesPage />} />
        <Route path="/crm/settings" element={<CrmSettingsPage />} />
        <Route path="/crm/settings/*" element={<CrmSettingsPage />} />
        <Route path="/crm/audit" element={<CrmAuditPage />} />
        <Route path="/crm/audit/:moduleId/:recordId" element={<CrmAuditRecordPage />} />
        <Route path="/crm/audit/:moduleId" element={<CrmAuditModulePage />} />
        <Route path="/crm/*" element={<CrmUnknownRoutePage />} />
        <Route path="/settings" element={<SettingsOverviewPage />} />
        <Route path="/settings/users" element={<SettingsUsersPage />} />
        <Route path="/settings/groups" element={<SettingsGroupsPage />} />
        <Route path="/settings/audit" element={<SettingsAuditPage />} />
        <Route
          path="/settings/profiles"
          element={stub(
            "Security profiles",
            "Profile templates port next; use Directory groups for access today.",
          )}
        />
        <Route path="/settings/*" element={<SettingsOverviewPage />} />
        <Route path="/finance" element={<FinanceOverviewPage />} />
        <Route path="/finance/customers/new" element={<FinanceCustomerNewPage />} />
        <Route path="/finance/customers/:customerId" element={<FinanceCustomerDetailPage />} />
        <Route path="/finance/customers" element={<FinanceCustomersPage />} />
        <Route path="/finance/invoices/new" element={<FinanceInvoiceNewPage />} />
        <Route path="/finance/invoices/:invoiceId" element={<FinanceInvoiceDetailPage />} />
        <Route path="/finance/invoices" element={<FinanceInvoicesPage />} />
        <Route path="/finance/items/new" element={<FinanceItemNewPage />} />
        <Route path="/finance/items/:itemId" element={<FinanceItemDetailPage />} />
        <Route path="/finance/items" element={<FinanceItemsPage />} />
        <Route path="/finance/sales/accounts/:customerId" element={<FinanceCustomerDetailPage />} />
        <Route path="/finance/sales/accounts" element={<FinanceAccountsPage />} />
        <Route path="/finance/sales/invoices/new" element={<FinanceInvoiceNewPage />} />
        <Route path="/finance/sales/invoices/:invoiceId" element={<FinanceInvoiceDetailPage />} />
        <Route path="/finance/sales/invoices" element={<FinanceInvoicesPage />} />
        <Route path="/finance/sales/payments-received" element={<FinancePaymentsReceivedPage />} />
        <Route
          path="/finance/sales/quotes"
          element={<FinanceComingSoonPage title="Quotes" />}
        />
        <Route
          path="/finance/sales/recurring-invoices"
          element={<FinanceComingSoonPage title="Recurring invoices" />}
        />
        <Route
          path="/finance/sales/retainer-invoices"
          element={<FinanceComingSoonPage title="Retainer invoices" />}
        />
        <Route
          path="/finance/sales/sales-orders"
          element={<FinanceComingSoonPage title="Sales orders" />}
        />
        <Route
          path="/finance/sales/credit-notes"
          element={<FinanceComingSoonPage title="Credit notes" />}
        />
        <Route path="/finance/purchases/vendors" element={<FinanceVendorsPage />} />
        <Route
          path="/finance/purchases/bills"
          element={<FinanceComingSoonPage title="Bills" />}
        />
        <Route
          path="/finance/purchases/recurring-bills"
          element={<FinanceComingSoonPage title="Recurring bills" />}
        />
        <Route
          path="/finance/purchases/payments-made"
          element={<FinanceComingSoonPage title="Payments made" />}
        />
        <Route
          path="/finance/purchases/vendor-credits"
          element={<FinanceComingSoonPage title="Vendor credits" />}
        />
        <Route
          path="/finance/banking"
          element={<FinanceComingSoonPage title="Banking" description="Bank feeds and reconciliation." />}
        />
        <Route
          path="/finance/accounting"
          element={<FinanceComingSoonPage title="Accounting" />}
        />
        <Route path="/finance/reports" element={<FinanceComingSoonPage title="Reports" />} />
        <Route path="/finance/taxes" element={<FinanceComingSoonPage title="Taxes" />} />
        <Route path="/finance/documents" element={<FinanceComingSoonPage title="Documents" />} />
        <Route path="/finance/compliance" element={<FinanceComingSoonPage title="Compliance" />} />
        <Route path="/finance/time" element={<FinanceComingSoonPage title="Time tracking" />} />
        <Route
          path="/finance/settings/*"
          element={<FinanceComingSoonPage title="Finance settings" description="Templates, tax, and invoice numbering." />}
        />
        <Route path="/human-resources" element={stub("Human resources", "HR modules and audit.")} />
        <Route path="/human-resources/*" element={stub("Human resources", "HR modules and audit.")} />
        <Route path="/marketing" element={stub("Marketing", "Campaigns and social.")} />
        <Route path="/marketing/*" element={stub("Marketing", "Campaigns and social.")} />
        <Route path="/blogs" element={stub("Blogs", "Posts, analytics, and comments.")} />
        <Route path="/blogs/*" element={stub("Blogs", "Posts, analytics, and comments.")} />
        <Route path="/knowledge" element={<Navigate to="/knowledge/bases" replace />} />
        <Route path="/knowledge/bases" element={<KnowledgeBasePage />} />
        <Route path="/knowledge/bases/:baseId" element={<KnowledgeBasePage />} />
        <Route path="/knowledge/bases/:baseId/libraries" element={<KnowledgeLibrariesPage />} />
        <Route path="/knowledge/bases/:baseId/libraries/:libraryId" element={<KnowledgeLibraryPage />} />
        <Route path="/knowledge/bases/:baseId/books" element={<KnowledgeBooksPage />} />
        <Route path="/knowledge/bases/:baseId/books/:bookKey" element={<KnowledgeBookPage />} />
        <Route path="/knowledge/bases/:baseId/articles" element={<KnowledgeArticlesPage />} />
        <Route path="/knowledge/bases/:baseId/articles/new" element={<KnowledgeArticlePage mode="create" />} />
        <Route
          path="/knowledge/bases/:baseId/articles/:articleId/edit"
          element={<KnowledgeArticlePage mode="edit" />}
        />
        <Route path="/knowledge/bases/:baseId/articles/:articleId" element={<KnowledgeArticleViewPage />} />
        <Route path="/knowledge/articles" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/articles/new" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/articles/:articleId/edit" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/articles/:articleId" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/libraries" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/libraries/:libraryId" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/books" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/books/:bookKey" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/library" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/collections" element={<KnowledgeLegacyRedirect />} />
        <Route path="/knowledge/collections/:collectionKey" element={<KnowledgeCollectionRedirect />} />
        <Route path="/knowledge/*" element={<Navigate to="/knowledge/bases" replace />} />
        <Route path="/projects" element={stub("Projects", "Tasks, issues, and project lists.")} />
        <Route path="/projects/*" element={stub("Projects", "Tasks, issues, and project lists.")} />
        <Route
          path="/worksuite"
          element={stub("WorkSuite", "Open worksuite.atw.net for mail, calendar, and meet.")}
        />
        <Route
          path="/worksuite/*"
          element={stub("WorkSuite", "Open worksuite.atw.net for mail, calendar, and meet.")}
        />
        <Route path="/pay" element={<Navigate to="/" replace />} />
        <Route path="/pay/*" element={<Navigate to="/" replace />} />
        <Route path="/analytics" element={<Navigate to="/analytics/overview" replace />} />
        <Route path="/analytics/*" element={stub("Analytics", "Org analytics dashboards.")} />
        <Route path="/custom-apps" element={stub("Custom apps", "Builder and runtime pages port next.")} />
        <Route path="/custom-apps/*" element={stub("Custom apps", "Builder and runtime pages port next.")} />
        <Route
          path="/platform"
          element={stub("Platform", "Operator console lives on platform.atw.net for most tasks.")}
        />
        <Route
          path="/platform/*"
          element={stub("Platform", "Operator console lives on platform.atw.net for most tasks.")}
        />
        <Route path="/support" element={stub("Support", "Support tickets and help.")} />
        <Route path="/support/*" element={stub("Support", "Support tickets and help.")} />
        <Route path="/organization" element={<OrganizationOverviewPage />} />
        <Route path="/organization/:domain" element={<OrganizationDetailPage />} />
      </Route>

      <Route path="/dashboard/*" element={<DashboardLegacyRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function DashboardLegacyRedirect() {
  const location = useLocation();
  const path = location.pathname.replace(/^\/dashboard/, "") || "/";
  return <Navigate to={path} replace />;
}

function KnowledgeCollectionRedirect() {
  const { collectionKey = "" } = useParams();
  const session = useSession();
  const orgId = resolveKnowledgeOrgId(session.data?.user?.orgId);
  if (orgId) void refreshKnowledgeRegistryFromApi(orgId);
  const activeId = orgId ? getActiveKnowledgeBaseId(orgId) : "";
  if (!activeId) return <Navigate to="/knowledge/bases" replace />;
  return <Navigate to={bookPath(activeId, decodeURIComponent(collectionKey))} replace />;
}
